import { useMemo } from "react";
import { Popover, Tag } from "antd";
import { useFetch } from "@/hooks/FetchHook";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import type {
  ProvenanceColumn,
  ProvenanceField,
  ProvenancePayload,
  ProvenanceSource,
} from "./provenance.types";
import { HANDLING_LABELS } from "./provenance.types";
import { tablesInQuery, tokenizeQuery } from "./sqlIdentifiers";

interface TokenInfo {
  kind: "table" | "column";
  table: string;
  column?: string;
  description?: string;
  source?: ProvenanceField;
  load?: ProvenanceSource;
}

function TokenPopoverContent({ info }: { info: TokenInfo }) {
  const handling = info.source?.handling;
  return (
    <div className="max-w-[320px] text-sm">
      <div className="font-mono text-xs mb-1">
        {info.kind === "table" ? info.table : `${info.table}.${info.column}`}
      </div>
      {info.description && (
        <p className="text-gray-500 mb-2">{info.description}</p>
      )}
      {info.kind === "table" && info.load && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-gray-500">Fed by</span>
          <Tag className="m-0">{info.load.source_display_name}</Tag>
          {info.load.rows_loaded !== null && (
            <span className="tabular-nums text-xs">
              {info.load.rows_loaded.toLocaleString()} rows
            </span>
          )}
        </div>
      )}
      {info.kind === "column" && info.source?.source_field && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-gray-500">Fed by</span>
          <Tag className="m-0">{info.source.source_display_name}</Tag>
          <span className="font-mono text-xs">
            {info.source.source_dataset
              ? `${info.source.source_dataset}.${info.source.source_field}`
              : info.source.source_field}
          </span>
          {handling && (
            <Tag className="m-0">{HANDLING_LABELS[handling] ?? handling}</Tag>
          )}
        </div>
      )}
      {info.kind === "column" && info.source && !info.source.source_field && (
        <div className="text-gray-500 text-xs">Derived in Vivo</div>
      )}
    </div>
  );
}

/**
 * The agent's SQL, with the parts that name the data model made explorable.
 *
 * Only identifiers the registry actually knows become interactive, so a token
 * never promises an explanation the backend cannot give. When the flag is off or
 * nothing resolves, this renders exactly the plain query it always did.
 */
export default function SqlProvenance({ query }: { query: string }) {
  const hasProvenance = useFeatureFlag("data-provenance");
  const tables = useMemo(() => tablesInQuery(query), [query]);

  const { data } = useFetch({
    endpointSuffix: `provenance/?tables=${encodeURIComponent(tables.join(","))}`,
    enabled: hasProvenance && tables.length > 0,
  });
  const payload = data as ProvenancePayload | null;

  const lookup = useMemo(() => {
    const map = new Map<string, TokenInfo>();
    if (!payload) return map;

    const loadByTable = new Map<string, ProvenanceSource>();
    for (const source of payload.sources ?? []) {
      if (!loadByTable.has(source.target_table)) {
        loadByTable.set(source.target_table, source);
      }
    }
    for (const [table, load] of loadByTable) {
      map.set(table, { kind: "table", table, load });
    }

    const describedColumns = new Map<string, ProvenanceColumn>();
    for (const column of payload.columns ?? []) {
      describedColumns.set(column.target_column, column);
    }
    const mappedColumns = new Map<string, ProvenanceField>();
    for (const field of payload.fields ?? []) {
      // First mapping wins: a fan-out lists the same column once per target,
      // and any of them names the same source field.
      if (!mappedColumns.has(field.target_column)) {
        mappedColumns.set(field.target_column, field);
      }
    }

    for (const [name, column] of describedColumns) {
      map.set(name, {
        kind: "column",
        table: column.target_table,
        column: name,
        description: column.description,
        source: mappedColumns.get(name),
      });
    }
    for (const [name, field] of mappedColumns) {
      if (map.has(name)) continue;
      map.set(name, {
        kind: "column",
        table: field.target_table,
        column: name,
        source: field,
      });
    }
    return map;
  }, [payload]);

  if (!hasProvenance || lookup.size === 0) {
    return <code className="whitespace-pre-wrap">{query}</code>;
  }

  return (
    <code className="whitespace-pre-wrap">
      {tokenizeQuery(query).map((token) => {
        const info = token.isIdentifier
          ? lookup.get(token.text.toLowerCase())
          : undefined;
        if (!info) return <span key={token.key}>{token.text}</span>;
        return (
          <Popover
            key={token.key}
            trigger="click"
            content={<TokenPopoverContent info={info} />}
            placement="bottom"
          >
            <span
              role="button"
              tabIndex={0}
              data-testid="sql-provenance-token"
              className="cursor-pointer rounded-[3px] bg-[#DCEBF3] text-[#06709B] px-[2px] border-b border-dashed border-[#06709B]"
            >
              {token.text}
            </span>
          </Popover>
        );
      })}
    </code>
  );
}
